/**
 * V25 Phase 5b — Admin PWA helpers.
 *
 *   - registerAdminServiceWorker(): registers /admin-sw.js
 *   - subscribeToPush(api): registers a Web Push subscription with server
 *   - unsubscribeFromPush(api): unregisters
 *   - useInstallPrompt(): React hook exposing { canInstall, install, dismissed }
 */
import { useEffect, useState, useCallback } from "react";

const SW_PATH = "/admin-sw.js";
const SW_SCOPE = "/admin/";

export async function registerAdminServiceWorker() {
    if (typeof window === "undefined") return null;
    if (!("serviceWorker" in navigator)) return null;
    try {
        // Only register on admin routes
        if (!window.location.pathname.startsWith("/admin")) return null;
        // Swap manifest to admin-manifest for installability
        try {
            const link = document.querySelector('link[rel="manifest"]');
            if (link && !link.href.includes("admin-manifest")) {
                link.href = "/admin-manifest.webmanifest";
            }
            // Update meta title/theme for iOS standalone
            const title = document.querySelector('meta[name="apple-mobile-web-app-title"]');
            if (title) title.setAttribute("content", "AT Admin");
            const theme = document.querySelector('meta[name="theme-color"]');
            if (theme) theme.setAttribute("content", "#1E3A8A");
        } catch { /* noop */ }
        const reg = await navigator.serviceWorker.register(SW_PATH, { scope: SW_SCOPE });
        return reg;
    } catch (err) {
        console.warn("SW registration failed:", err);
        return null;
    }
}

export async function unregisterAdminServiceWorker() {
    if (!("serviceWorker" in navigator)) return false;
    const regs = await navigator.serviceWorker.getRegistrations();
    let ok = false;
    for (const r of regs) {
        if (r.scope.includes("/admin")) {
            await r.unregister();
            ok = true;
        }
    }
    // Also flush data cache
    const sw = navigator.serviceWorker.controller;
    if (sw) sw.postMessage("FLUSH_DATA_CACHE");
    return ok;
}

function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = atob(base64);
    const out = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) out[i] = rawData.charCodeAt(i);
    return out;
}

export async function subscribeToPush(api) {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        throw new Error("Push notifications are not supported by this browser");
    }
    const reg = await navigator.serviceWorker.ready;
    // Permission first
    const perm = await Notification.requestPermission();
    if (perm !== "granted") {
        throw new Error("Notification permission denied");
    }
    // Use the env-baked VAPID public key
    let publicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
    if (!publicKey) {
        // Fall back to server endpoint
        const { data } = await api.get("/admin/push/public-key");
        publicKey = data.public_key;
    }
    if (!publicKey) throw new Error("VAPID public key missing");
    // Reuse existing subscription if present, or create
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
        sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
    }
    const json = sub.toJSON();
    await api.post("/admin/push/subscribe", {
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
        user_agent: navigator.userAgent,
    });
    return sub;
}

export async function unsubscribeFromPush(api) {
    if (!("serviceWorker" in navigator)) return false;
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return false;
    const endpoint = sub.endpoint;
    await sub.unsubscribe();
    try { await api.post("/admin/push/unsubscribe", { endpoint }); } catch { /* ignore */ }
    return true;
}

export async function hasActivePushSubscription() {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return false;
    try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        return !!sub;
    } catch { return false; }
}

// ─────────────────────────── Install prompt hook ─────────────────────────

const DISMISSED_KEY = "at_admin_install_dismissed_v3";

export function useInstallPrompt() {
    const [event, setEvent] = useState(null);
    const [installed, setInstalled] = useState(
        typeof window !== "undefined" && window.matchMedia?.("(display-mode: standalone)").matches
    );
    const [dismissed, setDismissed] = useState(
        typeof window !== "undefined" && localStorage.getItem(DISMISSED_KEY) === "1"
    );

    useEffect(() => {
        const onPrompt = (e) => {
            e.preventDefault();
            setEvent(e);
        };
        const onInstalled = () => {
            setInstalled(true);
            setEvent(null);
        };
        window.addEventListener("beforeinstallprompt", onPrompt);
        window.addEventListener("appinstalled", onInstalled);
        return () => {
            window.removeEventListener("beforeinstallprompt", onPrompt);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    const install = useCallback(async () => {
        if (!event) return false;
        await event.prompt();
        const choice = await event.userChoice;
        setEvent(null);
        return choice.outcome === "accepted";
    }, [event]);

    const dismiss = useCallback(() => {
        setDismissed(true);
        try { localStorage.setItem(DISMISSED_KEY, "1"); } catch { /* noop */ }
    }, []);

    return {
        canInstall: !!event && !installed && !dismissed,
        installed,
        dismissed,
        install,
        dismiss,
    };
}
